import { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const isNative = () => !!(window.Capacitor?.isNativePlatform?.());

const GoogleIcon = () => (
  <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const AppleIcon = () => (
  <svg className="apple-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.44c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.53 3.95zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const EmailForm = ({ mode, onSuccess }) => {
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setWorking(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login/local';
      await axios.post(endpoint, form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: '0.25rem' }}>
      {mode === 'register' && (
        <input className="form-input" name="name" type="text" placeholder="Your name (optional)"
          value={form.name} onChange={set} style={{ marginBottom: '0.6rem' }} />
      )}
      <input className="form-input" name="email" type="email" placeholder="Email address"
        value={form.email} onChange={set} required style={{ marginBottom: '0.6rem' }} />
      <input className="form-input" name="password" type="password"
        placeholder={mode === 'register' ? 'Password (min 8 characters)' : 'Password'}
        value={form.password} onChange={set} required minLength={mode === 'register' ? 8 : undefined}
        style={{ marginBottom: '0.75rem' }} />
      {error && <p className="form-error" style={{ marginBottom: '0.6rem' }}>{error}</p>}
      <button type="submit" className="btn btn-primary" disabled={working} style={{ width: '100%' }}>
        {working ? '…' : mode === 'register' ? 'Create Account' : 'Sign In'}
      </button>
    </form>
  );
};

const FEATURES = [
  { icon: '📋', title: 'Maintenance Schedules', desc: 'Auto-advancing due dates for every recurring task — filters, HVAC, gutters, and seasonal prep.' },
  { icon: '🔨', title: 'Project Planner', desc: 'AI-generated materials lists, tools checklists, step-by-step plans, and cost estimates.' },
  { icon: '🏠', title: 'Room Records', desc: 'Document paint colors, flooring, outlets with breaker mapping, and fixture specs per room.' },
  { icon: '⚙️', title: 'Appliance Tracker', desc: 'Model numbers, warranties, and service history for every mechanical system in your home.' },
  { icon: '📦', title: 'Supply Inventory', desc: 'Track filters, bulbs, and batteries. Low-stock alerts keep your shopping list ready.' },
  { icon: '🤖', title: 'AI Planning', desc: 'Describe a project in plain English — Claude builds a full plan with materials, costs, and steps.' },
];

const Landing = () => {
  const { search } = useLocation();
  const isUnauthorized = new URLSearchParams(search).get('error') === 'unauthorized';
  const [appleError, setAppleError] = useState('');
  const [appleWorking, setAppleWorking] = useState(false);
  const [emailMode, setEmailMode] = useState(null);
  const signInRef = useRef(null);

  const scrollToSignIn = () => signInRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleAppleNative = async () => {
    setAppleError('');
    setAppleWorking(true);
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const result = await SignInWithApple.authorize({
        clientId: 'com.jessecomeau.homemaintenance',
        redirectURI: 'https://home.jessecomeau.com/api/auth/apple/callback',
        scopes: 'email name',
      });
      await axios.post('/api/auth/apple/token', {
        identityToken: result.response.identityToken,
        fullName: result.response.fullName,
      });
      window.location.href = '/';
    } catch (err) {
      if (err?.message !== 'The user canceled the sign-in flow.') {
        setAppleError('Apple Sign In failed. Please try again.');
      }
    } finally {
      setAppleWorking(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <span className="landing-header-logo">🏠</span>
        <span className="landing-header-brand">Home Maintenance</span>
        <button className="btn btn-primary landing-header-cta" onClick={scrollToSignIn}>
          Sign In
        </button>
      </header>

      {/* Hero */}
      <section className="landing-hero">
        <h1 className="landing-hero-title">Your home,<br />organized.</h1>
        <p className="landing-hero-sub">
          Track maintenance schedules, home improvement projects, appliances, rooms, and
          supplies — all in one place.
        </p>
        <button className="btn btn-primary landing-hero-btn" onClick={scrollToSignIn}>
          Get started →
        </button>
      </section>

      {/* Features */}
      <section className="landing-features">
        <div className="landing-features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="landing-feature-card">
              <span className="landing-feature-icon">{f.icon}</span>
              <h3 className="landing-feature-title">{f.title}</h3>
              <p className="landing-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sign in */}
      <section className="landing-signin" ref={signInRef}>
        <div className="login-card">
          <div className="login-logo">🏠</div>
          <h2 className="login-title">Welcome back</h2>
          <p className="login-subtitle">Sign in to manage your home</p>

          {(isUnauthorized || appleError) && (
            <p className="form-error" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
              {appleError || 'Sign in failed. Please try again.'}
            </p>
          )}

          <a href="/api/auth/google" className="btn-google">
            <GoogleIcon />
            Sign in with Google
          </a>

          {isNative() ? (
            <button className="btn-apple" onClick={handleAppleNative} disabled={appleWorking}>
              <AppleIcon />
              {appleWorking ? 'Signing in…' : 'Sign in with Apple'}
            </button>
          ) : (
            <a href="/api/auth/apple" className="btn-apple">
              <AppleIcon />
              Sign in with Apple
            </a>
          )}

          <div className="login-divider"><span>or</span></div>

          {emailMode === null && (
            <div style={{ display: 'flex', gap: '0.6rem', width: '100%' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEmailMode('login')}>
                Sign in with Email
              </button>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEmailMode('register')}>
                Create Account
              </button>
            </div>
          )}

          {emailMode !== null && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', width: '100%', marginBottom: '0.75rem' }}>
                <button className={`btn btn-sm${emailMode === 'login' ? ' btn-primary' : ' btn-secondary'}`}
                  style={{ flex: 1 }} onClick={() => setEmailMode('login')}>Sign In</button>
                <button className={`btn btn-sm${emailMode === 'register' ? ' btn-primary' : ' btn-secondary'}`}
                  style={{ flex: 1 }} onClick={() => setEmailMode('register')}>Create Account</button>
              </div>
              <EmailForm mode={emailMode} onSuccess={() => { window.location.href = '/'; }} />
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Landing;
